import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Sparkles, Image as ImageIcon, Target, ShieldCheck, Save, Zap,
  ArrowRight, Star, Check, ChevronDown, Play, TrendingUp, Clock, PoundSterling,
  Mail, MapPin as MapPinIcon, MessageCircle as MessageCircleIcon, Loader2, Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppOptIn from '../components/WhatsAppOptIn';
import TrustBar from '../components/TrustBar';
import ExitIntentModal from '../components/ExitIntentModal';
import HowItWorksVideo from '../components/HowItWorksVideo';
import AdPreviewCard from '../components/AdPreviewCard';
import SignupCount from '../components/SignupCount';
import StickyEmailCapture from '../components/StickyEmailCapture';
import { useExperiment } from '../hooks/useExperiment';
import { trackCta, trackEvent } from '../hooks/useAnalytics';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { features, testimonials, pricingPlans, faqs, stats, sampleAds, heroImages } from '../mock';
import { getIndustryVariant } from '../config/industryVariants';

const iconMap = { Sparkles, Image: ImageIcon, Target, ShieldCheck, Save, Zap };

const Landing = () => {
  const navigate = useNavigate();
  const { industry: industrySlug } = useParams();
  const { user } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  // Industry variant pulled from /for/:industry routes. When matched, the
  // hero copy + sample inputs are personalised for that vertical (cafés,
  // salons, tradespeople, etc) so paid traffic sees ad-aligned messaging.
  const industryVariant = getIndustryVariant(industrySlug);

  // Default business shown when the visitor lands on `/` (no industry slug).
  // The hero auto-generates a real AI ad for this business on first visit so
  // the magic happens before they have to click anything.
  const DEFAULT_SAMPLE = {
    sampleBusiness: 'Bloom & Petal Florists',
    sampleBrief: 'Fresh seasonal bouquets, hand-tied with British-grown blooms, free local delivery within 5 miles.',
  };

  // "Try-before-you-signup" inline ad generator state
  const [bizName, setBizName] = useState(industryVariant?.sampleBusiness || DEFAULT_SAMPLE.sampleBusiness);
  const [bizBrief, setBizBrief] = useState(industryVariant?.sampleBrief || DEFAULT_SAMPLE.sampleBrief);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);  // { previewId, headline, body, cta, hashtags }
  const [previewError, setPreviewError] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadSending, setLeadSending] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [autoGenStarted, setAutoGenStarted] = useState(false);

  useEffect(() => {
    if (industrySlug) {
      trackEvent('industry_landing_view', { industry: industrySlug });
    }
  }, [industrySlug]);

  // Auto-generate a sample ad on page load — visitor sees the magic happen
  // BEFORE clicking anything. One per session (sessionStorage flag set
  // INSIDE the setTimeout to survive React StrictMode double-mount). Logged
  // -in users + returning visitors skip. Backed by the IP-rate-limit
  // (3/IP/24h) on the backend for extra protection.
  useEffect(() => {
    if (user) return;
    if (typeof window === 'undefined') return;
    const SESSION_KEY = 'mbap_autopreview_done';
    if (sessionStorage.getItem(SESSION_KEY)) return;
    if (preview || previewLoading || autoGenStarted) return;
    if (!bizName.trim() || bizBrief.trim().length < 5) return;
    // Small delay so the visitor sees the "Watch our AI write your first
    // ad" prompt for a beat before the generation starts — builds intent.
    const t = setTimeout(() => {
      // Re-check inside the timer (StrictMode-safe): second pass of the
      // effect cancels this timer, so only the surviving run flips the flag.
      if (sessionStorage.getItem(SESSION_KEY)) return;
      sessionStorage.setItem(SESSION_KEY, '1');
      setAutoGenStarted(true);
      trackEvent('public_preview_auto', { variant: heroVariant, industry: industrySlug || '' });
      handleGeneratePreview();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live A/B: "control" = our existing scroll-stopping headline;
  // "urgent" = a more direct, outcome-led variant for IG traffic.
  // Variant is deterministic per visitor session and tracked in analytics.
  const heroVariant = useExperiment('hero_v2', ['control', 'urgent']);
  const baseHeroCopy = heroVariant === 'urgent'
    ? {
      h1Pre: 'Your first AI ad in ',
      h1Highlight: '30 seconds',
      h1Post: '. No card required.',
      sub: 'UK small businesses use MyBusinessAdPro to generate scroll-stopping Meta ads, captions and images — without writing a word or hiring a designer.',
      cta: 'Try It Free — No Card Needed',
    }
    : {
      h1Pre: 'Scroll-stopping ',
      h1Highlight: 'social ads',
      h1Post: ', written by AI.',
      sub: 'From your local takeaway to the high street salon — describe your business in a sentence and get Meta-ready ads with copy, hashtags & visuals in 30 seconds.',
      cta: 'Create Your First Ad Free',
    };
  // Industry-specific copy overrides A/B copy when present
  const heroCopy = industryVariant
    ? {
      h1Pre: industryVariant.h1Pre,
      h1Highlight: industryVariant.h1Highlight,
      h1Post: industryVariant.h1Post,
      sub: industryVariant.sub,
      cta: industryVariant.cta,
    }
    : baseHeroCopy;

  const handleGeneratePreview = async (e) => {
    e?.preventDefault?.();
    if (!bizName.trim() || bizBrief.trim().length < 5) {
      toast({ title: 'Add a few details', description: 'Tell us your business name and a short brief (one sentence is enough).' });
      return;
    }
    setPreviewLoading(true);
    setPreviewError('');
    trackEvent('public_preview_started', { variant: heroVariant, industry: industrySlug || '' });
    try {
      const { data } = await api.post('/public/preview-ad', {
        businessName: bizName.trim(),
        businessType: industrySlug || 'other',
        brief: bizBrief.trim(),
      });
      setPreview(data);
      trackEvent('public_preview_generated', { variant: heroVariant, industry: industrySlug || '' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong — please try again.';
      setPreviewError(detail);
      toast({ title: 'AI is busy', description: detail });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCaptureLead = async (e) => {
    e?.preventDefault?.();
    if (!leadEmail.trim() || !leadEmail.includes('@')) {
      toast({ title: 'Enter a valid email' });
      return;
    }
    setLeadSending(true);
    trackEvent('public_lead_submit', { variant: heroVariant, industry: industrySlug || '', hasPreview: !!preview });
    try {
      await api.post('/auth/magic-link', {
        email: leadEmail.trim().toLowerCase(),
        name: bizName.trim() || '',
        previewId: preview?.previewId || '',
        source: industrySlug ? `industry_${industrySlug}` : 'landing_preview',
      });
      setLeadSent(true);
      trackEvent('public_lead_captured', { variant: heroVariant, industry: industrySlug || '' });
    } catch (err) {
      toast({ title: 'Could not send link', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLeadSending(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate(`/signup?plan=${planId}`);
      return;
    }
    setCheckoutLoading(planId);
    try {
      const { data } = await api.post('/billing/create-checkout-session', {
        planId,
        originUrl: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setCheckoutLoading(null);
      toast({
        title: 'Could not start checkout',
        description: err?.response?.data?.detail || 'Please try again.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfaf6]">
      <Navbar />
      <TrustBar />
      <ExitIntentModal preview={!leadSent ? preview : null} bizName={bizName} />
      <StickyEmailCapture preview={!leadSent ? preview : null} bizName={bizName} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50" />
        <div className="absolute top-20 -left-20 w-96 h-96 bg-orange-200/40 rounded-full blur-3xl" />
        <div className="absolute top-40 -right-20 w-96 h-96 bg-rose-200/40 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-8 pb-14 sm:pt-12 sm:pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="bg-white/80 backdrop-blur border-orange-200 text-orange-700 font-medium px-3 py-1 rounded-full">
                <Sparkles className="w-3 h-3 mr-1.5" /> Built for UK small businesses
              </Badge>
              <Badge
                variant="outline"
                data-testid="hero-uk-territory-chip"
                className="hidden sm:inline-flex bg-white/80 backdrop-blur border-slate-200 text-slate-700 font-medium px-3 py-1 rounded-full"
                aria-label="Available across the whole of the UK"
              >
                <span className="mr-1.5" aria-hidden="true">🇬🇧</span> England · Wales · Scotland · NI
              </Badge>
            </div>
            <h1 className="mt-3 sm:mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              {heroCopy.h1Pre}<span className="bg-gradient-to-r from-orange-500 to-rose-500 bg-clip-text text-transparent">{heroCopy.h1Highlight}</span>{heroCopy.h1Post}
            </h1>
            <p className="mt-3 sm:mt-5 text-sm sm:text-base lg:text-lg xl:text-xl text-slate-600 max-w-xl leading-relaxed">
              {heroCopy.sub}
            </p>

            {/* Trust micro-line: above-the-fold antidote to the "card upfront" worry */}
            <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-emerald-700 font-semibold flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" />
              7-day free trial · 3 ads + 3 images free · no card needed
            </p>

            {/* "Try-before-you-signup" — show real AI-generated ad on the
                landing page itself, then trade the unblurred result for an
                email (magic-link signup, no password required). Industry
                /for/:slug visitors get the inputs pre-filled with a
                vertical-relevant example so they can click 'Generate'
                straight away. */}
            {!preview ? (
              <form
                onSubmit={handleGeneratePreview}
                className="mt-4 sm:mt-6 max-w-xl space-y-2.5"
                data-testid="hero-preview-form"
              >
                {previewLoading && (
                  <div className="rounded-xl bg-gradient-to-r from-orange-50 to-rose-50 border border-orange-200 px-4 py-3 flex items-center gap-3" data-testid="hero-auto-gen-banner">
                    <Loader2 className="w-5 h-5 text-orange-500 animate-spin flex-shrink-0" />
                    <div className="text-sm text-slate-800">
                      <b>AI is writing your first ad…</b> Watch the right ↗
                    </div>
                  </div>
                )}
                <input
                  type="text"
                  value={bizName}
                  onChange={(e) => setBizName(e.target.value)}
                  placeholder="Your business name (e.g. Bloom & Petal Florists)"
                  aria-label="Business name"
                  className="w-full h-11 sm:h-12 px-4 rounded-xl border border-slate-300 bg-white/95 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                  data-testid="hero-preview-business-name"
                />
                <textarea
                  value={bizBrief}
                  onChange={(e) => setBizBrief(e.target.value)}
                  placeholder="What do you want to advertise? (one sentence is enough)"
                  rows={2}
                  aria-label="Ad brief"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white/95 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base resize-none"
                  data-testid="hero-preview-brief"
                />
                <Button
                  type="submit"
                  size="lg"
                  disabled={previewLoading}
                  className="w-full h-12 sm:h-14 px-6 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white font-bold rounded-xl text-sm sm:text-base shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 group whitespace-nowrap disabled:opacity-70"
                  data-testid="hero-preview-cta"
                >
                  {previewLoading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Writing your ad…</>
                  ) : (
                    <><Sparkles className="w-5 h-5 mr-2" /> Show me my free ad <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" /></>
                  )}
                </Button>
                {previewError && (
                  <p className="text-sm text-rose-600" data-testid="hero-preview-error">{previewError}</p>
                )}
              </form>
            ) : !leadSent ? (
              <form
                onSubmit={handleCaptureLead}
                className="mt-7 max-w-xl space-y-3"
                data-testid="hero-lead-form"
              >
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-emerald-800 text-sm flex items-start gap-2">
                  <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><b>Here's your ad ↗</b> Drop your email below to unlock it + get <b>2 more free</b>. No card, no commitment.</span>
                </div>
                <input
                  type="email"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  required
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@yourbusiness.co.uk"
                  className="w-full h-12 px-4 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 text-base"
                  data-testid="hero-lead-email"
                />
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={leadSending}
                    className="flex-1 h-14 px-6 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white font-bold rounded-xl text-base shadow-lg shadow-orange-500/30 group"
                    data-testid="hero-lead-submit"
                  >
                    {leadSending ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending link…</>
                    ) : (
                      <><Mail className="w-5 h-5 mr-2" /> Email me my sign-in link</>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      trackCta('hero_lead_use_password', { variant: heroVariant });
                      navigate(`/signup?email=${encodeURIComponent(leadEmail || '')}`);
                    }}
                    className="h-14 px-5 rounded-xl border-slate-300 text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                    data-testid="hero-lead-use-password"
                  >
                    Or use a password
                  </Button>
                </div>
                <p className="text-xs text-slate-500 flex items-center gap-1.5">
                  <Lock className="w-3 h-3" />
                  We only use your email to sign you in. Never spam. Unsubscribe anytime.
                </p>
              </form>
            ) : (
              <div
                className="mt-7 max-w-xl rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-6"
                data-testid="hero-lead-success"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                    <Check className="w-6 h-6 text-white" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <div className="font-extrabold text-slate-900 text-lg">Check your inbox 📥</div>
                    <p className="mt-1 text-slate-700 text-sm">
                      We sent a one-click sign-in link to <b>{leadEmail}</b>. Click it to open
                      your dashboard and finish creating this ad. The link works for 15 minutes.
                    </p>
                    <p className="mt-3 text-xs text-slate-500">
                      Don't see it? Check Promotions / Spam, or
                      {' '}<button
                        type="button"
                        onClick={() => setLeadSent(false)}
                        className="underline text-orange-600 font-semibold"
                        data-testid="hero-lead-retry"
                      >try a different email</button>.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <p className="mt-2 text-xs text-slate-500">
              No credit card. No commitment. Cancel any time.{' '}
              <button
                type="button"
                onClick={() => { trackCta('hero_see_how'); document.getElementById('how-it-works-video')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="text-slate-700 underline hover:text-slate-900 font-semibold"
              >
                See how it works ↓
              </button>
            </p>

            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <SignupCount testid="hero-signup-count" />
            </div>

            <div className="mt-6 flex items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className={`w-8 h-8 rounded-full ring-2 ring-white bg-gradient-to-br ${
                      ['from-orange-300 to-rose-400','from-amber-300 to-orange-400','from-rose-300 to-pink-400','from-emerald-300 to-teal-400'][i-1]
                    }`} />
                  ))}
                </div>
                <span><strong className="text-slate-900">850+</strong> UK businesses</span>
              </div>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                <span className="ml-1"><strong className="text-slate-900">4.9</strong></span>
              </div>
            </div>
          </div>

          {/* Right column: swap demo video for the generated AdPreviewCard
              the moment the visitor generates one. Card stays blurred-ish
              until they drop their email (mild bait — strong enough to
              get the conversion, soft enough to still build trust). */}
          {preview ? (
            <div className="relative" data-testid="hero-preview-wrapper">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-200/40 to-rose-200/40 rounded-3xl blur-2xl" />
              <div className="relative">
                <AdPreviewCard ad={preview} businessName={bizName} blur={!leadSent} />
                {!leadSent && (
                  <div className="absolute inset-0 flex items-end justify-center p-6 pointer-events-none">
                    <div className="bg-white/95 backdrop-blur rounded-xl shadow-2xl border border-slate-200 px-4 py-3 pointer-events-auto flex items-center gap-2 max-w-xs">
                      <Lock className="w-4 h-4 text-orange-500" />
                      <span className="text-sm font-semibold text-slate-900">Enter your email above to unlock</span>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setPreview(null); setLeadSent(false); setLeadEmail(''); trackCta('hero_preview_regenerate'); }}
                className="absolute -bottom-3 -left-3 bg-white rounded-full shadow-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                data-testid="hero-preview-regenerate"
              >
                ↺ Try a different brief
              </button>
            </div>
          ) : (
            <div className="relative" data-testid="hero-video-wrapper">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-200/40 to-rose-200/40 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl shadow-2xl shadow-rose-200/50 border border-slate-200 overflow-hidden bg-slate-900">
                <video
                  src="https://customer-assets.emergentagent.com/job_local-biz-ads-2/artifacts/sjdkwphg_MBAP%20APPROVED%20VIDEO%201.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="w-full h-auto block"
                  data-testid="hero-demo-video"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl border border-slate-200 p-4 flex items-center gap-3 animate-pulse">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">UK ASA</div>
                  <div className="text-sm font-semibold text-slate-900">Compliant ✓</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 -mt-10">
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map(s => (
            <div key={s.label} className="text-center">
              <div className="text-3xl lg:text-4xl font-extrabold text-slate-900">{s.value}</div>
              <div className="text-sm text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SEE-HOW-IT-WORKS VIDEO */}
      <HowItWorksVideo onPrimaryCta={() => { trackCta('video_below_signup', { variant: heroVariant }); navigate('/signup'); }} />

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <Badge variant="outline" className="border-slate-300 text-slate-700 mb-4">Features</Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            Everything you need. Nothing you don't.
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Built for busy owners who'd rather be running their shop than fiddling with ad managers.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => {
            const Icon = iconMap[f.icon];
            return (
              <div key={i} className="group bg-white rounded-2xl p-7 border border-slate-200 hover:border-orange-300 hover:shadow-xl hover:shadow-orange-100/50 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-100 to-rose-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Icon className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="mt-5 font-bold text-lg text-slate-900">{f.title}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto">
            <Badge className="bg-white/10 text-white border-0 mb-4">How it works</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">From idea to ad in 30 seconds</h2>
            <p className="mt-4 text-lg text-slate-300">No agency, no fuss. Three steps.</p>
          </div>

          <div className="mt-16 grid lg:grid-cols-3 gap-6">
            {[
              { n: '01', icon: Clock, t: 'Describe your business', d: 'Tell us what you sell, who you want to reach, and any offer. Plain English. One sentence is enough.' },
              { n: '02', icon: Sparkles, t: 'AI does the heavy lifting', d: 'Our model writes ad copy, picks visuals, suggests hashtags, and runs UK ASA compliance — in seconds.' },
              { n: '03', icon: TrendingUp, t: 'Post & grow', d: 'Copy to Meta Ads Manager, set your budget, and watch local customers come knocking.' },
            ].map((step, i) => (
              <div key={i} className="relative bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8">
                <div className="text-7xl font-extrabold text-white/[0.06] absolute top-4 right-6">{step.n}</div>
                <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                  <step.icon className="w-6 h-6 text-orange-400" />
                </div>
                <h3 className="mt-5 text-xl font-bold">{step.t}</h3>
                <p className="mt-2 text-slate-400 leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE ADS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <Badge variant="outline" className="border-slate-300 text-slate-700 mb-4">Real examples</Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">Ads that actually sound human</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {sampleAds.map(ad => (
            <div key={ad.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow">
              <img src={ad.image} alt="" className="w-full h-56 object-cover" />
              <div className="p-6">
                <div className="text-xs uppercase tracking-wider text-orange-600 font-semibold">{ad.business}</div>
                <h3 className="mt-2 font-bold text-slate-900 text-lg">{ad.headline}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{ad.body}</p>
                <div className="mt-3 text-xs text-orange-600">{ad.hashtags}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-gradient-to-b from-orange-50 to-rose-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto">
            <Badge variant="outline" className="bg-white border-orange-200 text-orange-700 mb-4">Loved by local owners</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">Real businesses. Real results.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 shadow-sm border border-slate-100">
                <div className="flex gap-1">
                  {Array.from({length: t.rating}).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="mt-4 text-slate-700 leading-relaxed">“{t.quote}”</p>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <div className="font-semibold text-slate-900">{t.name}</div>
                  <div className="text-sm text-slate-500">{t.business}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE — top-rated user-generated ads (4★+) of the month */}
      <ShowcaseSection />

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <Badge variant="outline" className="border-slate-300 text-slate-700 mb-4"><PoundSterling className="w-3 h-3 mr-1" />Pricing</Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">Simple, fair pricing</h2>
          <p className="mt-4 text-lg text-slate-600">Cheaper than one hour with an agency. Cancel anytime.</p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {pricingPlans.map(p => (
            <div key={p.id} className={`relative rounded-2xl p-6 sm:p-7 border transition-all ${
              p.popular
                ? 'bg-slate-950 text-white border-slate-900 shadow-xl md:scale-105'
                : 'bg-white text-slate-900 border-slate-200 hover:border-slate-300'
            }`}>
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-400 to-rose-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                  MOST POPULAR
                </div>
              )}
              <div className="text-sm font-semibold uppercase tracking-wider opacity-70">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-5xl font-extrabold">£{p.price}</span>
                <span className="text-sm opacity-60">/{p.period}</span>
              </div>
              <p className={`mt-2 text-sm ${p.popular ? 'text-slate-300' : 'text-slate-600'}`}>{p.description}</p>
              <div className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${p.popular ? 'bg-white/10 text-orange-300' : 'bg-emerald-50 text-emerald-700'}`}>
                <Check className="w-3 h-3" /> 7-day free trial
              </div>
              <Button
                onClick={() => handleSubscribe(p.id)}
                disabled={checkoutLoading === p.id}
                className={`mt-5 w-full rounded-xl font-semibold h-11 ${
                  p.popular ? 'bg-orange-500 hover:bg-orange-400 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                {checkoutLoading === p.id ? 'Redirecting…' : p.cta}
              </Button>
              <ul className="mt-6 space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${p.popular ? 'text-orange-400' : 'text-emerald-500'}`} />
                    <span className={p.popular ? 'text-slate-200' : 'text-slate-700'}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 lg:px-8 py-24">
        <div className="text-center">
          <Badge variant="outline" className="border-slate-300 text-slate-700 mb-4">FAQ</Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">Quick answers</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-xl border border-slate-200 px-5">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CONTACT */}
      <section id="contact" className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="bg-white rounded-3xl border border-slate-200 p-8 lg:p-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge variant="outline" className="border-slate-300 text-slate-700 mb-4">Get in touch</Badge>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              Questions? We'd love to hear from you.
            </h2>
            <p className="mt-3 text-slate-600">
              Drop us a line and we'll get back to you within one working day. Whether it's pricing, partnerships, or you just want a friendly hello.
            </p>
          </div>
          <div className="space-y-4">
            <a href="mailto:contact@mybusinessadpro.co.uk" className="flex items-start gap-4 p-5 rounded-2xl border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all group">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-100 to-rose-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <Mail className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Email us</div>
                <div className="font-bold text-slate-900 mt-0.5">contact@mybusinessadpro.co.uk</div>
                <div className="text-sm text-slate-500 mt-1">Replies within 1 working day</div>
              </div>
            </a>
            <div className="flex items-start gap-4 p-5 rounded-2xl border border-slate-200">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                <MessageCircleIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">WhatsApp updates</div>
                <div className="font-bold text-slate-900 mt-0.5">Tips, news & launch perks</div>
                <div className="text-sm text-slate-500 mt-1">Sign up below — we send 1–2 messages a month.</div>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 rounded-2xl border border-slate-200">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                <MapPinIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Based in</div>
                <div className="font-bold text-slate-900 mt-0.5">United Kingdom</div>
                <div className="text-sm text-slate-500 mt-1">Built for UK small businesses</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-10 lg:p-16">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-orange-500/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-rose-500/30 rounded-full blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Your next customer is scrolling right now.
            </h2>
            <p className="mt-4 text-lg text-slate-300">
              Set up takes 60 seconds. First ad is free. No card needed.
            </p>
            <Button
              onClick={() => navigate('/signup')}
              size="lg"
              className="mt-7 bg-white text-slate-900 hover:bg-orange-50 font-semibold rounded-xl px-7 h-14 text-base group"
            >
              Start creating ads <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      <WhatsAppOptIn />

      <Footer />
    </div>
  );
};

// Inline showcase section — fetches /public/showcase and renders a marquee-style
// carousel of top-rated, marketing-consented user generations.
const ShowcaseSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/public/showcase?limit=9')
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (!loading && items.length === 0) return null;  // hide until we have content

  return (
    <section id="showcase" className="bg-slate-50 border-y border-slate-200 py-20" data-testid="showcase-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Badge variant="outline" className="border-slate-300 text-slate-700 mb-4">
            <Star className="w-3 h-3 mr-1 fill-amber-400 text-amber-400" />
            Loved this month
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            Real ads, real businesses
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Hand-picked 4★+ ads &amp; videos that real UK small businesses generated with MyBusinessAdPro this month. Used here with their permission.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="showcase-grid">
          {items.map(it => (
            <div key={it.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg transition" data-testid={`showcase-item-${it.id}`}>
              {it.thumbnail && (
                <div className="aspect-square bg-slate-100 overflow-hidden">
                  <img src={it.thumbnail} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-1 text-amber-500 mb-2">
                  {[...Array(it.rating)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                {it.headline && (
                  <div className="font-bold text-slate-900 leading-snug mb-2">{it.headline}</div>
                )}
                {it.comment && (
                  <p className="text-sm text-slate-600 italic">“{it.comment}”</p>
                )}
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                  {it.firstName ? `— ${it.firstName}` : '— anonymous'}{it.businessType ? ` · ${it.businessType}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Landing;
